import * as Sentry from '@sentry/nextjs';
import { useCallback, useEffect } from 'react';
import useSwr from 'swr';
import { formatUnits } from 'viem';
import { useAccount, useAccountEffect, usePublicClient } from 'wagmi';

import { NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID } from '../../../constants/chain';
import { REFRESH_INTERVAL } from '../../../constants/rpc';
import { nativeTokenBalanceSelectors, useBalanceStore } from '../../../store/balance';
import {
  EVENT_NAME,
  IDENTIFY_TYPES,
  USER_PROPERTIES,
  identifyUserProperties,
  logEvent,
} from '../../../utils/amplitude';
import { trackError } from '../../../utils/errors';

interface Props {
  onSetCanTrade: (canTrade: boolean) => void;
}

const AppUpdater = ({ onSetCanTrade }: Props) => {
  const { chain } = useAccount();
  const publicClient = usePublicClient({ chainId: chain?.id });
  const { setNativeTokenBalance } = useBalanceStore(nativeTokenBalanceSelectors);

  const { address } = useAccount();

  useAccountEffect({
    async onConnect({ address, connector }) {
      if (address !== undefined && connector !== undefined && publicClient !== undefined) {
        // fetch native balance
        try {
          const balance = await publicClient.getBalance({ address });
          setNativeTokenBalance(formatUnits(balance, 18));
        } catch (err: unknown) {
          console.debug(
            'There was an error fetching the native balance on connecting the wallet',
            err,
          );
          if (err instanceof Error) {
            trackError(err);
          }
        }
        // event tracking
        logEvent({
          name: EVENT_NAME.WALLET_CONNECTED,
          properties: { address: address, connectorName: connector?.name },
        });
        identifyUserProperties({
          type: IDENTIFY_TYPES.POST_INSERT,
          key: USER_PROPERTIES.WALLETS_USED,
          value: address,
        });
        identifyUserProperties({
          type: IDENTIFY_TYPES.SET,
          key: USER_PROPERTIES.WALLET_CONNECTED,
          value: connector?.name,
        });
        identifyUserProperties({
          type: IDENTIFY_TYPES.SET,
          key: USER_PROPERTIES.ADDRESS_CONNECTED,
          value: address,
        });
        // set sentry tags
        Sentry.setTag('user.account.wallet', connector?.name || 'unknown');
        Sentry.setTag('user.account.address', address);
        if (chain) {
          identifyUserProperties({
            type: IDENTIFY_TYPES.SET,
            key: USER_PROPERTIES.CHAIN_SELECTED,
            value: chain?.id.toString(),
          });
          identifyUserProperties({
            type: IDENTIFY_TYPES.SET,
            key: USER_PROPERTIES.CHAIN_SELECTED_NAME,
            value: NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[chain.id],
          });
          Sentry.setTag('user.account.selected_chain', chain?.id?.toString() ?? 'unknown');
        }
      }
    },
    onDisconnect() {
      logEvent({
        name: EVENT_NAME.WALLET_DISCONNECTED,
      });
    },
  });

  const { data: canTradeResult } = useSwr(
    address ? ['/api/retrieve', address] : null,
    ([url, add]) =>
      fetch(url, {
        method: 'POST',
        body: JSON.stringify({ address: add }),
      }).then((res) => res.json()),
  );

  useEffect(() => {
    if (typeof canTradeResult === 'boolean') {
      onSetCanTrade(canTradeResult);
    }
  }, [canTradeResult, onSetCanTrade]);

  const fetchBalance = useCallback(async () => {
    if (!address) return;
    if (!publicClient) return;

    const balance = await publicClient.getBalance({ address });
    return balance;
  }, [address, publicClient]);

  useSwr(chain?.id ? `fetch-balance-${chain?.id}` : null, fetchBalance, {
    onSuccess: (balance: bigint | undefined) => {
      if (balance) setNativeTokenBalance(formatUnits(balance, 18));
    },
    refreshInterval: REFRESH_INTERVAL,
    refreshWhenHidden: false,
  });

  return null;
};

export default AppUpdater;
